/**
 * Height of the *layout* viewport in CSS pixels, with pinch-zoom cancelled out.
 *
 * `visualViewport.height` measures the visual viewport, so zooming in shrinks it exactly the
 * way an opening keyboard does — at 2x zoom it halves. Every keyboard heuristic in this app
 * derives the keyboard height from `someFullHeight - visualViewport.height`, so without this
 * correction a pinch reads as a ~400px keyboard: footers translate off-screen, modals collapse
 * their max-height, and JobDetailsMobile's "keyboard closed" detector fires and exits edit mode.
 *
 * `height * scale` converts back to layout-viewport pixels. The relation holds at any scale,
 * and when a keyboard genuinely is open the visual viewport already excludes it, so callers
 * subtracting this from the full height still get the keyboard's height.
 *
 * At scale 1 — Android, and every un-zoomed iOS screen — this returns exactly
 * `visualViewport.height`, so behaviour on those paths is unchanged.
 */
export function layoutViewportHeight(vv: VisualViewport): number {
    // `|| 1` is deliberate, not defensive noise. useNativeKeyboardFix is mounted app-wide and
    // 20+ components position footers and modal heights off its output, so a missing or zero
    // `scale` on some old Android WebView would otherwise produce NaN, emit an invalid
    // `--keyboard-height: NaNpx`, and silently break keyboard handling across all of them.
    // Falling back to 1 makes the worst case byte-identical to the pre-zoom behaviour.
    return vv.height * (vv.scale || 1);
}
